/**
 * bri-variant-display: weergave van de variantkiezer (zie blocks/bri-variant-display.liquid). Alleen weergave, de optienamen
 * en waarden in de productdata blijven zoals ze zijn.
 *  1. Het label van een kleuroptie toont "Color" en van een maatoptie "Size" (Colour, Maat, Taille, ... worden daarvoor omgezet).
 *  2. Kleurstalen: heeft ELKE kleurwaarde van het product een bekende kleur, dan krijgt de rij vlakke kleurvierkanten in
 *     de opmaak van de swatches van het thema. Is er een waarde zonder bekende kleur, dan blijft de hele rij tekstknoppen.
 *  3. Een tekstdeel (bv. " (Almost sold out)") wordt uit de zichtbare knoppen gehaald.
 * De kiezer wordt door het thema bij elke keuze opnieuw opgebouwd; een MutationObserver zet de weergave daarna terug.
 */

(() => {
const COLOR_OPTION = /^(colou?rs?|couleurs?|kleur(en)?|farbe[n]?|colore|colori|f(ä|a)rg|cor(es)?)$/i;
const SIZE_OPTION = /^(sizes?|maten?|maat|tailles?|gr(o|ö|oe)(ss|ß)e|storlek|tallas?|taglia|taglie|tamanho|tama(n|ñ)o)$/i;

/** @type {Promise<Record<string, string>> | null} */
let namesPromise = null;

/**
 * @param {string} url
 * @returns {Promise<Record<string, string>>}
 */
function loadNames(url) {
  if (!namesPromise) {
    namesPromise = fetch(url)
      .then((response) => (response.ok ? response.json() : {}))
      .catch(() => ({}));
  }
  return namesPromise;
}

/** @param {string} value */
function handleize(value) {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** De naam zonder toelichting: wat voor een '(' of ' - ' staat. */
function cleanName(value) {
  return value.split('(')[0].split(' - ')[0].replace(/[【】]/g, '').trim();
}

/** @param {string} raw  bv. ";black=#1c1c1c~50,55;white=#ffffff;" */
function parseSpec(raw) {
  /** @type {Record<string, string>} */
  const map = {};
  for (const part of raw.split(';')) {
    const at = part.indexOf('=');
    if (at < 1) continue;
    map[part.slice(0, at)] = part.slice(at + 1).split('~')[0].trim();
  }
  return map;
}

/**
 * Zet een kleurwaarde om in een CSS-achtergrond, of null als het geen kleur is.
 * @param {string | undefined} spec
 */
function toBackground(spec) {
  if (!spec) return null;
  if (/^#[0-9a-f]{3,8}$/i.test(spec)) return spec;
  const two = spec.match(/^(#[0-9a-f]{3,8})\+(#[0-9a-f]{3,8})$/i);
  if (two) return `linear-gradient(135deg, ${two[1]} 50%, ${two[2]} 50%)`;
  if (spec === 'multi') return 'conic-gradient(#e4403f, #f1cd3a, #3a9a56, #2f6fd0, #7a4aa8, #e4403f)';
  return null;
}

class BriVariantDisplay extends HTMLElement {
  /** @type {MutationObserver | null} */
  #observer = null;
  #queued = false;
  #running = false;
  #abort = new AbortController();

  connectedCallback() {
    const picker = this.#picker();
    if (!picker) return;
    this.#observer = new MutationObserver(() => this.#queue());
    this.#observer.observe(picker, { childList: true, subtree: true });
    picker.addEventListener('change', () => this.#queue(), { signal: this.#abort.signal });
    this.#queue();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#abort.abort();
  }

  #picker() {
    return this.closest('.shopify-section')?.querySelector('variant-picker') ?? null;
  }

  #queue() {
    if (this.#queued) return;
    this.#queued = true;
    requestAnimationFrame(() => {
      this.#queued = false;
      this.#refresh();
    });
  }

  async #refresh() {
    if (this.#running) return;
    this.#running = true;
    const picker = this.#picker();
    if (!picker) {
      this.#running = false;
      return;
    }
    // tijdens het schrijven geen eigen meldingen
    this.#observer?.disconnect();
    try {
      for (const fieldset of picker.querySelectorAll('fieldset.variant-option--buttons')) {
        const legend = fieldset.querySelector('legend');
        const name = this.#optionName(legend);
        if (legend && this.dataset.normalizeLabels === 'true') this.#normalizeLabel(legend, name);
        if (this.dataset.removeText) this.#removeText(fieldset, this.dataset.removeText);
        if (this.dataset.swatches === 'true' && COLOR_OPTION.test(name)) await this.#swatches(fieldset, legend);
      }
    } finally {
      this.#running = false;
      this.#observer?.observe(picker, { childList: true, subtree: true });
    }
  }

  /** @param {Element | null} legend */
  #optionName(legend) {
    const first = legend && legend.firstChild;
    return ((first && first.textContent) || '').trim();
  }

  /**
   * @param {Element} legend
   * @param {string} name
   */
  #normalizeLabel(legend, name) {
    const first = legend.firstChild;
    if (!first || first.nodeType !== Node.TEXT_NODE) return;
    const wanted = COLOR_OPTION.test(name) ? 'Color' : SIZE_OPTION.test(name) ? 'Size' : '';
    if (wanted && name !== wanted) first.textContent = first.textContent.replace(name, wanted);
  }

  /**
   * @param {Element} fieldset
   * @param {string} text
   */
  #removeText(fieldset, text) {
    for (const label of fieldset.querySelectorAll('.variant-option__button-label__text')) {
      if (label.textContent && label.textContent.includes(text)) {
        label.textContent = label.textContent.split(text).join('').trim();
      }
    }
  }

  /**
   * @param {Element} fieldset
   * @param {Element | null} legend
   */
  async #swatches(fieldset, legend) {
    /** @type {NodeListOf<HTMLLabelElement>} */
    const labels = fieldset.querySelectorAll('label.variant-option__button-label');
    if (!labels.length) return;
    if (fieldset.classList.contains('bri-swatches') && [...labels].every((label) => label.querySelector('.bri-swatch'))) {
      this.#syncValue(fieldset, legend);
      return;
    }

    const own = parseSpec(this.dataset.colors || '');
    /** @type {Array<string | null>} */
    const backgrounds = [];
    /** @type {Record<string, string> | null} */
    let names = null;
    for (const label of labels) {
      const input = label.querySelector('input');
      const value = input ? input.value : '';
      let background = toBackground(own[handleize(value)] || own[handleize(cleanName(value))]);
      if (!background) {
        if (!names) names = await loadNames(this.dataset.namesUrl || '');
        const key = handleize(cleanName(value).replace(/[/&+-]/g, ' '));
        background = toBackground(names[key]);
      }
      backgrounds.push(background);
    }
    // een rij is altijd gelijk: alle kleuren bekend, of geen stalen
    if (backgrounds.some((background) => !background)) return;

    fieldset.classList.add('variant-option--swatches', 'bri-swatches');
    fieldset.setAttribute('data-bri-count', String(labels.length));
    labels.forEach((label, index) => {
      label.classList.add('variant-option__button-label--has-swatch');
      if (label.querySelector('.bri-swatch')) return;
      const swatch = document.createElement('span');
      swatch.className = 'swatch swatch--unscaled bri-swatch';
      swatch.style.setProperty('--swatch-background', /** @type {string} */ (backgrounds[index]));
      label.querySelector('input')?.after(swatch);
    });
    this.#syncValue(fieldset, legend);
  }

  /**
   * Toont de gekozen kleur in het label, zoals het thema dat bij zijn eigen stalen doet.
   * @param {Element} fieldset
   * @param {Element | null} legend
   */
  #syncValue(fieldset, legend) {
    if (!legend) return;
    const checked = fieldset.querySelector('input:checked');
    let holder = legend.querySelector('.bri-swatch-value');
    if (!holder) {
      holder = document.createElement('span');
      holder.className = 'variant-option__swatch-value bri-swatch-value';
      legend.append(holder);
    }
    const value = checked ? /** @type {HTMLInputElement} */ (checked).value : '';
    if (holder.textContent !== value) holder.textContent = value;
  }
}

if (!customElements.get('bri-variant-display')) customElements.define('bri-variant-display', BriVariantDisplay);
})();
